import { api } from './client'

export interface HealthEvent {
    id?: string
    animal: string
    condition: string
    description?: string
    severity: 'Mild' | 'Moderate' | 'Severe' | 'Critical'
    start_date: string
    end_date?: string
    status: 'Ongoing' | 'Recovered' | 'Chronic' | 'Died'
    recorded_by?: string
    symptoms?: any[]
    treatments?: any[]
}

export interface Vaccination {
    id?: string
    animal: string
    vaccine_name: string
    vaccination_date: string
    next_due_date?: string
    batch_lot_number?: string
    administered_by?: string
    notes?: string
    is_overdue?: boolean
    days_until_due?: number
}

export interface Deworming {
    id?: string
    animal: string
    product: string
    date_administered: string
    next_due_date?: string
    dosage?: string
    method?: string
    is_overdue?: boolean
}

export interface Quarantine {
    id?: string
    animal: string
    reason: string
    start_date: string
    expected_end_date: string
    actual_end_date?: string
    released: boolean
    release_notes?: string
    recorded_by?: string
}

export interface Treatment {
    id?: string
    health_event: string
    medication: string
    dosage: string
    frequency: string
    cost?: number
    start_date: string
    end_date?: string
    withdrawal_days?: number
}

export interface Mortality {
    id?: string
    animal: string
    date_of_death: string
    cause?: string
    symptoms_before_death?: string
    actions_taken?: string
    necropsy_performed: boolean
    necropsy_findings?: string
    recorded_by?: string
}

export interface HealthCheck {
    id?: string
    animal: string
    check_date: string
    check_type: 'Routine' | 'Emergency' | 'Follow-up' | 'Preventive'
    temperature?: number
    weight?: number
    heart_rate?: number
    respiratory_rate?: number
    body_condition_score?: number
    findings: string
    recommendations?: string
    veterinarian?: string
    next_check_date?: string
    recorded_by?: string
}

export const healthService = {
    // Health Events
    async getHealthEvents(animalId?: string) {
        const params = animalId ? { animal_id: animalId } : {}
        return api.get<HealthEvent[]>('health/health-events/', { params })
    },

    async createHealthEvent(data: Omit<HealthEvent, 'id'>) {
        return api.post<HealthEvent>('health/health-events/', data)
    },

    async updateHealthEvent(id: string, data: Partial<HealthEvent>) {
        return api.put<HealthEvent>(`health/health-events/${id}/`, data)
    },

    async deleteHealthEvent(id: string) {
        return api.delete(`health/health-events/${id}/`)
    },

    // Vaccinations
    async getVaccinations(animalId?: string) {
        const params = animalId ? { animal_id: animalId } : {}
        return api.get<Vaccination[]>('health/vaccinations/', { params })
    },

    async createVaccination(data: Omit<Vaccination, 'id'>) {
        return api.post<Vaccination>('health/vaccinations/', data)
    },

    async updateVaccination(id: string, data: Partial<Vaccination>) {
        return api.put<Vaccination>(`health/vaccinations/${id}/`, data)
    },

    async deleteVaccination(id: string) {
        return api.delete(`health/vaccinations/${id}/`)
    },

    // Deworming
    async getDewormingRecords(animalId?: string) {
        const params = animalId ? { animal_id: animalId } : {}
        return api.get<Deworming[]>('health/deworming/', { params })
    },

    async createDewormingRecord(data: Omit<Deworming, 'id'>) {
        return api.post<Deworming>('health/deworming/', data)
    },

    async updateDewormingRecord(id: string, data: Partial<Deworming>) {
        return api.put<Deworming>(`health/deworming/${id}/`, data)
    },

    async deleteDewormingRecord(id: string) {
        return api.delete(`health/deworming/${id}/`)
    },

    // Quarantine
    async getQuarantines(animalId?: string) {
        const params = animalId ? { animal_id: animalId } : {}
        return api.get<Quarantine[]>('health/quarantines/', { params })
    },

    async createQuarantine(data: Omit<Quarantine, 'id'>) {
        return api.post<Quarantine>('health/quarantines/', data)
    },

    async updateQuarantine(id: string, data: Partial<Quarantine>) {
        return api.put<Quarantine>(`health/quarantines/${id}/`, data)
    },

    async releaseQuarantine(id: string, releaseNotes?: string) {
        return api.post(`health/quarantines/${id}/release/`, { release_notes: releaseNotes })
    },

    // Mortality
    async getMortalityRecords(animalId?: string) {
        const params = animalId ? { animal_id: animalId } : {}
        return api.get<Mortality[]>('health/mortality/', { params })
    },

    async createMortalityRecord(data: Omit<Mortality, 'id'>) {
        return api.post<Mortality>('health/mortality/', data)
    },

    // Health Checks
    async getHealthChecks(animalId?: string) {
        const params = animalId ? { animal_id: animalId } : {}
        return api.get<HealthCheck[]>('health/health-checks/', { params })
    },

    async createHealthCheck(data: Omit<HealthCheck, 'id'>) {
        return api.post<HealthCheck>('health/health-checks/', data)
    },

    // Treatments
    async getTreatments(healthEventId?: string) {
        const params = healthEventId ? { health_event_id: healthEventId } : {}
        return api.get<Treatment[]>('health/treatments/', { params })
    },

    async createTreatment(data: Omit<Treatment, 'id'>) {
        return api.post<Treatment>('health/treatments/', data)
    },

    // Health Alerts
    async getHealthAlerts() {
        return api.get<any[]>('health/animals/health-alerts/')
    },
}
